import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  analyzeFileNormalization,
  commitFileNormalization,
  reviewFileNormalization,
} from '../fileNormalization/service';
import type {
  FileNormalizationAnalyzeResult,
} from '../fileNormalization/types';
import FileNormalizationReviewPanel from '../fileNormalization/components/FileNormalizationReviewPanel';
import {
  confirmImportedManufacturingOrdersImport,
  createImportedManufacturingOrdersImportFlowState,
  getImportedManufacturingOrdersImportSummary,
  runImportedManufacturingOrdersImportPreview,
} from '../importFlows/importedManufacturingOrdersImportFlow';
import type {
  ImportedManufacturingOrdersImportFlowState,
} from '../importFlows/importedManufacturingOrdersImportFlow';
import type {
  ImportedManufacturingOrderImportBlockingIssue,
  ImportedManufacturingOrderImportCorrection,
} from '../types/industrialCost';

const IMPORTER_KEY = 'imported_manufacturing_orders';

type ImportStep = 'structure' | 'mapping' | 'confirm';

type MappableField = {
  key: string;
  label: string;
  required?: boolean;
};

const MAPPABLE_FIELDS: MappableField[] = [
  {
    key: 'manufacturing_order_number',
    label: 'OF',
    required: true,
  },
  {
    key: 'planned_quantity_value',
    label: 'Cantidad prevista',
    required: true,
  },
  { key: 'free_product_ref', label: 'Referencia producto' },
  { key: 'customer_number', label: 'Cliente' },
  { key: 'customer_order_number', label: 'Pedido cliente' },
  {
    key: 'planning_duration_source_value',
    label: 'Horas fabricación pendientes',
  },
  { key: 'machine', label: 'Máquina' },
  { key: 'pending_meters', label: 'Metros pendientes' },
  { key: 'produced_meters', label: 'Metros fabricados' },
  { key: 'delivered_meters', label: 'Metros entregados' },
  { key: 'dispatch_date', label: 'Fecha expedición' },
  {
    key: 'theoretical_dispatch_date',
    label: 'Fecha expedición teórica',
  },
  { key: 'notes', label: 'Observaciones' },
];

type IndustrialCostManufacturingOrdersSourceDialogProps = {
  open: boolean;
  file: File | null;
  onClose: () => void;
  onImported: (message: string) => void;
};

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const detail = (
      error as { response?: { data?: { detail?: unknown } } }
    ).response?.data?.detail;

    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}


function toCommitMapping(
  mapping: Record<string, string | null>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(mapping).filter(
      (entry): entry is [string, string] =>
        typeof entry[1] === 'string' && entry[1].trim().length > 0,
    ),
  );
}

const ZEROABLE_BLOCKING_FIELDS = new Set([
  'pending_meters',
  'produced_meters',
  'delivered_meters',
  'planning_duration_source_value',
]);

function isZeroableBlockingIssue(
  issue: ImportedManufacturingOrderImportBlockingIssue,
) {
  if (
    !issue.manufacturing_order_number ||
    !ZEROABLE_BLOCKING_FIELDS.has(issue.field) ||
    issue.raw_value === null
  ) {
    return false;
  }

  const parsed = Number(
    issue.raw_value.replace(',', '.'),
  );

  return Number.isFinite(parsed) && parsed < 0;
}

function withPlanningHoursDefault(
  mapping: Record<string, string | null>,
): Record<string, string | null> {
  if (!mapping.planning_duration_source_value) {
    return mapping;
  }

  if (mapping.planning_duration_source_unit) {
    return mapping;
  }

  return {
    ...mapping,
    planning_duration_source_unit: 'hours',
  };
}

export default function IndustrialCostManufacturingOrdersSourceDialog({
  open,
  file,
  onClose,
  onImported,
}: IndustrialCostManufacturingOrdersSourceDialogProps) {
  const [step, setStep] = useState<ImportStep>('structure');
  const [normalization, setNormalization] =
    useState<FileNormalizationAnalyzeResult | null>(null);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [headerRowNumber, setHeaderRowNumber] = useState('1');
  const [flow, setFlow] =
    useState<ImportedManufacturingOrdersImportFlowState>(() =>
      createImportedManufacturingOrdersImportFlowState(),
    );
  const [draftMapping, setDraftMapping] = useState<
    Record<string, string | null>
  >({});
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const preview = flow.preview;
  const summary = useMemo(
    () => getImportedManufacturingOrdersImportSummary(flow),
    [flow],
  );

  const zeroableBlockingIssues = useMemo(
    () =>
      preview?.blocking_issues.filter(
        isZeroableBlockingIssue,
      ) ?? [],
    [preview],
  );

  const canClose = !loading && !busyAction;

  async function analyzeStructure() {
    if (!file) return;

    setLoading(true);
    setErrorMessage(null);
    setStep('structure');
    setNormalization(null);
    setFlow(createImportedManufacturingOrdersImportFlowState(file));
    setDraftMapping({});

    try {
      // IND-002: el fichero de OF pendientes se reconoce por sus columnas en
      // el servidor, se llame como se llame (p. ej. «… (1).xlsx»).
      const nextNormalization = await analyzeFileNormalization(
        file,
        IMPORTER_KEY,
      );

      setNormalization(nextNormalization);
      setSelectedSheet(nextNormalization.selected_sheet ?? '');
      setHeaderRowNumber(
        String(nextNormalization.header_row_index + 1),
      );
      setDraftMapping(
        withPlanningHoursDefault(
          nextNormalization.applied_column_mapping ?? {},
        ),
      );
    } catch (error) {
      setErrorMessage(
        getErrorMessage(
          error,
          'No se pudo analizar la estructura del archivo de OF pendientes.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open || !file) return;
    void analyzeStructure();
  }, [open, file]);

  async function handleSheetChange(sheetName: string) {
    if (!file || !sheetName) return;

    setBusyAction(true);
    setErrorMessage(null);

    try {
      const reviewed = await reviewFileNormalization(
        file,
        IMPORTER_KEY,
        0,
        sheetName,
      );

      setNormalization(reviewed);
      setSelectedSheet(reviewed.selected_sheet ?? '');
      setHeaderRowNumber(
        String(reviewed.header_row_index + 1),
      );
      setDraftMapping(
        withPlanningHoursDefault(
          reviewed.applied_column_mapping ?? {},
        ),
      );
      setFlow(createImportedManufacturingOrdersImportFlowState(file));
    } catch (error) {
      setErrorMessage(
        getErrorMessage(
          error,
          'No se pudo cargar la muestra de la hoja seleccionada.',
        ),
      );
    } finally {
      setBusyAction(false);
    }
  }

  async function handleUseStructure() {
    if (!file || !normalization) return;

    const parsedHeaderRow = Number(headerRowNumber);

    if (!Number.isInteger(parsedHeaderRow) || parsedHeaderRow < 1) {
      setErrorMessage(
        'Selecciona visualmente una fila válida como cabecera.',
      );
      return;
    }

    setBusyAction(true);
    setErrorMessage(null);

    try {
      let finalNormalization = normalization;
      const selectedHeaderRowIndex = parsedHeaderRow - 1;

      const structureChanged =
        selectedSheet !== (normalization.selected_sheet ?? '') ||
        selectedHeaderRowIndex !== normalization.header_row_index;

      if (structureChanged) {
        finalNormalization = await reviewFileNormalization(
          file,
          IMPORTER_KEY,
          selectedHeaderRowIndex,
          selectedSheet || null,
        );

        setNormalization(finalNormalization);
        setSelectedSheet(finalNormalization.selected_sheet ?? '');
        setHeaderRowNumber(
          String(finalNormalization.header_row_index + 1),
        );
      }

      const initialMapping = withPlanningHoursDefault(
        finalNormalization.applied_column_mapping ?? {},
      );

      await commitFileNormalization({
        importer_key: IMPORTER_KEY,
        sheet_name: finalNormalization.selected_sheet,
        header_row_index: finalNormalization.header_row_index,
        data_start_row_index: finalNormalization.header_row_index + 1,
        column_mapping: toCommitMapping(initialMapping),
        known_columns: finalNormalization.detected_columns,
      });

      const nextFlow = await runImportedManufacturingOrdersImportPreview(
        createImportedManufacturingOrdersImportFlowState(file),
        {
          file,
          mapping: initialMapping,
          normalization: {
            sheet_name: finalNormalization.selected_sheet,
            header_row_index: finalNormalization.header_row_index,
          },
        },
      );

      setFlow(nextFlow);

      if (nextFlow.status === 'error' || !nextFlow.preview) {
        setErrorMessage(
          nextFlow.error ??
            'No se pudo validar el archivo de OF pendientes.',
        );
        return;
      }

      setDraftMapping(
        withPlanningHoursDefault(nextFlow.mapping),
      );
      setStep('mapping');
    } catch (error) {
      setErrorMessage(
        getErrorMessage(
          error,
          'No se pudo guardar la estructura seleccionada.',
        ),
      );
    } finally {
      setBusyAction(false);
    }
  }

  async function handleSaveMapping() {
    if (!file || !normalization || !preview) return;

    setBusyAction(true);
    setErrorMessage(null);

    try {
      const effectiveMapping = withPlanningHoursDefault(draftMapping);

      const nextFlow = await runImportedManufacturingOrdersImportPreview(
        flow,
        {
          file,
          mapping: effectiveMapping,
          normalization: {
            sheet_name: normalization.selected_sheet,
            header_row_index: normalization.header_row_index,
          },
        },
      );

      setFlow(nextFlow);

      if (nextFlow.status === 'error' || !nextFlow.preview) {
        setErrorMessage(
          nextFlow.error ??
            'No se pudo validar el mapeo seleccionado.',
        );
        return;
      }

      if (nextFlow.preview.missing_required_fields.length > 0) {
        setDraftMapping(effectiveMapping);
        setErrorMessage(
          `Faltan campos obligatorios por mapear: ${nextFlow.preview.missing_required_fields.join(', ')}.`,
        );
        return;
      }

      const committedMapping = withPlanningHoursDefault(nextFlow.mapping);

      await commitFileNormalization({
        importer_key: IMPORTER_KEY,
        sheet_name: normalization.selected_sheet,
        header_row_index: normalization.header_row_index,
        data_start_row_index: normalization.header_row_index + 1,
        column_mapping: toCommitMapping(committedMapping),
        known_columns: nextFlow.preview.columns,
      });

      setDraftMapping(committedMapping);
      setStep('confirm');
    } catch (error) {
      setErrorMessage(
        getErrorMessage(
          error,
          'No se pudo guardar el mapeo de OF pendientes.',
        ),
      );
    } finally {
      setBusyAction(false);
    }
  }

  function mergeCorrections(
    current: ImportedManufacturingOrderImportCorrection[],
    additions: ImportedManufacturingOrderImportCorrection[],
  ) {
    const merged = new Map<
      string,
      ImportedManufacturingOrderImportCorrection
    >();

    [...current, ...additions].forEach((item) => {
      merged.set(
        `${item.manufacturing_order_number}::${item.field}`,
        item,
      );
    });

    return Array.from(merged.values());
  }

  async function applyCorrections(
    additions: ImportedManufacturingOrderImportCorrection[],
  ) {
    if (!file || !normalization || additions.length === 0) {
      return;
    }

    setBusyAction(true);
    setErrorMessage(null);

    try {
      const corrections = mergeCorrections(
        flow.corrections,
        additions,
      );

      const nextFlow =
        await runImportedManufacturingOrdersImportPreview(
          flow,
          {
            file,
            mapping: withPlanningHoursDefault(
              draftMapping,
            ),
            corrections,
            normalization: {
              sheet_name: normalization.selected_sheet,
              header_row_index:
                normalization.header_row_index,
            },
          },
        );

      setFlow(nextFlow);

      if (nextFlow.status === 'error' || !nextFlow.preview) {
        setErrorMessage(
          nextFlow.error ??
            'No se pudieron aplicar las correcciones seleccionadas.',
        );
      }
    } catch (error) {
      setErrorMessage(
        getErrorMessage(
          error,
          'No se pudieron aplicar las correcciones seleccionadas.',
        ),
      );
    } finally {
      setBusyAction(false);
    }
  }

  async function handleSetIssueZero(
    issue: ImportedManufacturingOrderImportBlockingIssue,
  ) {
    if (!isZeroableBlockingIssue(issue)) {
      return;
    }

    await applyCorrections([
      {
        manufacturing_order_number:
          issue.manufacturing_order_number as string,
        field: issue.field,
        action: 'set_zero',
      },
    ]);
  }

  async function handleSetAllNegativesZero() {
    const corrections =
      zeroableBlockingIssues.map((issue) => ({
        manufacturing_order_number:
          issue.manufacturing_order_number as string,
        field: issue.field,
        action: 'set_zero' as const,
      }));

    await applyCorrections(corrections);
  }

  async function handleConfirm() {
    if (!file || !preview || !summary.canContinue) return;

    setBusyAction(true);
    setErrorMessage(null);

    try {
      const nextState = await confirmImportedManufacturingOrdersImport({
        ...flow,
        file,
        mapping: withPlanningHoursDefault(draftMapping),
      });

      setFlow(nextState);

      if (
        nextState.status !== 'imported' &&
        nextState.status !== 'imported_with_issues'
      ) {
        setErrorMessage(
          nextState.error ??
            'No se pudo confirmar la importación de OF pendientes.',
        );
        return;
      }

      onImported(
        nextState.resultMessage ?? 'Importación de OF completada.',
      );
    } catch (error) {
      setErrorMessage(
        getErrorMessage(
          error,
          'No se pudo confirmar la importación de OF pendientes.',
        ),
      );
    } finally {
      setBusyAction(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={canClose ? onClose : undefined}
      fullWidth
      maxWidth="xl"
    >
      <DialogTitle>Configurar OF / planificación pendiente</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.25}>
          <Alert severity="info" variant="outlined">
            Comprueba primero la hoja y la fila de encabezados. Después
            revisa el mapeo y confirma la publicación de la fotografía de OF.
          </Alert>

          {loading ? (
            <Alert severity="info">
              Analizando únicamente la estructura del archivo...
            </Alert>
          ) : null}

          {errorMessage ? (
            <Alert severity="error">{errorMessage}</Alert>
          ) : null}

          {step === 'structure' && normalization ? (
            <FileNormalizationReviewPanel
              result={normalization}
              selectedSheetName={selectedSheet}
              selectedHeaderRowIndex={Math.max(
                0,
                Number(headerRowNumber) - 1,
              )}
              isReviewing={busyAction}
              isContinuing={busyAction}
              onSheetChange={(sheetName) =>
                void handleSheetChange(sheetName)
              }
              onHeaderRowChange={(rowIndex) =>
                setHeaderRowNumber(String(rowIndex + 1))
              }
              onContinue={() => void handleUseStructure()}
            />
          ) : null}

          {step === 'mapping' && preview ? (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                <Stack spacing={0.75}>
                  <Typography sx={{ fontWeight: 900 }}>
                    Mapeo de columnas
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Revisa solo lo necesario. Los cambios se validan una vez
                    al guardar el mapeo, no con cada desplegable.
                  </Typography>
                </Stack>
              </Paper>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 1.5,
                }}
              >
                {MAPPABLE_FIELDS.map((field) => (
                  <TextField
                    key={field.key}
                    select
                    size="small"
                    label={`${field.label}${field.required ? ' *' : ''}`}
                    value={draftMapping[field.key] ?? ''}
                    onChange={(event) =>
                      setDraftMapping((current) => ({
                        ...current,
                        [field.key]: event.target.value || null,
                      }))
                    }
                    disabled={busyAction}
                  >
                    <MenuItem value="">Sin asignar</MenuItem>
                    {preview.columns.map((column) => (
                      <MenuItem key={`${field.key}-${column}`} value={column}>
                        {column}
                      </MenuItem>
                    ))}
                  </TextField>
                ))}
              </Box>

              <Alert severity="info" variant="outlined">
                La duración pendiente se interpreta en horas cuando se ha
                asignado su columna, igual que en la fuente operativa actual.
              </Alert>

              <Stack direction="row" justifyContent="space-between">
                <Button
                  onClick={() => setStep('structure')}
                  disabled={busyAction}
                >
                  Volver
                </Button>

                <Button
                  variant="contained"
                  onClick={() => void handleSaveMapping()}
                  disabled={busyAction}
                >
                  {busyAction
                    ? 'Guardando...'
                    : 'Guardar mapeo y continuar'}
                </Button>
              </Stack>
            </Stack>
          ) : null}

          {step === 'confirm' && preview ? (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                <Stack spacing={1}>
                  <Typography sx={{ fontWeight: 900 }}>
                    {preview.blocking_issues.length > 0
                      ? 'Archivo requiere revisión'
                      : 'Archivo listo para publicar'}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Filas detectadas: {preview.coverage_summary.total_rows}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    OF nuevas: {preview.coverage_summary.new_manufacturing_orders_count}
                    {' · '}OF existentes:{' '}
                    {preview.coverage_summary.existing_manufacturing_orders_count}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Referencias de producto detectadas:{' '}
                    {preview.detected_product_refs.length}
                    {' · '}Advertencias: {preview.row_warnings.length}
                    {' · '}Bloqueos:{' '}
                    {preview.blocking_issues.length}
                  </Typography>
                </Stack>
              </Paper>

              {preview.blocking_issues.length > 0 ? (
                <Stack spacing={1}>
                  <Alert severity="error" variant="outlined">
                    Hay {preview.blocking_issues.length} incidencia(s)
                    bloqueante(s). El snapshot actual se mantiene y esta
                    fuente no se publicará hasta corregirlas.
                  </Alert>

                  {zeroableBlockingIssues.length > 0 ? (
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, borderRadius: 2 }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 900 }}>
                            {zeroableBlockingIssues.length}{' '}
                            valor(es) negativo(s) pueden pasar a 0
                          </Typography>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            El Excel original no se modifica. PROA
                            conservará el valor original y la corrección.
                          </Typography>
                        </Box>

                        <Button
                          variant="contained"
                          color="warning"
                          onClick={() =>
                            void handleSetAllNegativesZero()
                          }
                          disabled={busyAction}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {busyAction
                            ? 'Aplicando...'
                            : 'Sustituir todos los negativos por 0'}
                        </Button>
                      </Stack>
                    </Paper>
                  ) : null}

                  {preview.blocking_issues
                    .slice(0, 20)
                    .map((issue, index) => (
                      <Paper
                        key={`${issue.manufacturing_order_number ?? 'row'}-${issue.field}-${index}`}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                        }}
                      >
                        <Stack spacing={0.35}>
                          <Typography
                            sx={{ fontWeight: 900 }}
                          >
                            {issue.manufacturing_order_number
                              ? `OF ${issue.manufacturing_order_number}`
                              : `Fila ${issue.source_row_number}`}
                          </Typography>

                          <Typography variant="body2">
                            {issue.field_label}
                            {' · Valor recibido: '}
                            <strong>
                              {issue.raw_value ?? 'vacío'}
                            </strong>
                          </Typography>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            Fila Excel {issue.source_row_number}
                            {issue.source_column
                              ? ` · Columna ${issue.source_column}`
                              : ''}
                            {' · '}
                            {issue.message}
                          </Typography>

                          {isZeroableBlockingIssue(issue) ? (
                            <Box sx={{ pt: 0.5 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                onClick={() =>
                                  void handleSetIssueZero(issue)
                                }
                                disabled={busyAction}
                              >
                                Sustituir por 0
                              </Button>
                            </Box>
                          ) : null}
                        </Stack>
                      </Paper>
                    ))}

                  {preview.blocking_issues.length > 20 ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Se muestran las primeras 20 incidencias de{' '}
                      {preview.blocking_issues.length}.
                    </Typography>
                  ) : null}
                </Stack>
              ) : null}

              {flow.corrections.length > 0 ? (
                <Alert severity="success" variant="outlined">
                  {flow.corrections.length} corrección(es) aplicada(s)
                  en esta importación. El Excel original permanece
                  sin cambios.
                </Alert>
              ) : null}

              {preview.row_warnings.length > 0 ? (
                <Alert severity="warning" variant="outlined">
                  Hay {preview.row_warnings.length} advertencia(s)
                  no bloqueante(s). Revisa su contenido antes de publicar.
                </Alert>
              ) : null}

              <Stack direction="row" justifyContent="flex-start">
                <Button
                  onClick={() => setStep('mapping')}
                  disabled={busyAction}
                >
                  Volver al mapeo
                </Button>
              </Stack>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={!canClose}>
          Cancelar
        </Button>

        {step === 'confirm' ? (
          <Button
            variant="contained"
            color="success"
            onClick={() => void handleConfirm()}
            disabled={busyAction || !summary.canContinue}
          >
            {busyAction ? 'Publicando...' : 'Confirmar carga'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
